/**
 * 객체 설정 Utils
 *
 * @since 2018-05-08
 * @author kdydesign.kim
 */

import {i18n} from '~/plugins/i18n'
import {
  weekFormatter,
  monthFormatter,
  yearFormatter,
  rangeFormatter
} from 'timeObjectFormatter'
import { isJsonString } from '~/utils/json'

/**
 * 주소 객체 형태
 *
 * @param objType
 * @returns {*}
 */
export function getAddrObjType (objType) {
  if (!objType) return objType
  if (objType.indexOf('address') > -1) {
    return _.split(objType, '.')[1]
  }
}

/**
 * 주소 객체 버전
 *
 * @param objType
 * @returns {*}
 */
export function getAddrVersion (objType) {
  if (objType.indexOf('address') > -1) {
    if (objType.indexOf('domain') > -1) {
      return 4
    } else {
      return _.split(objType, '.')[2]
    }
  }
}

/**
 * 주소 객체 addr_obj_type을 key로 변환
 *
 * @param addrObjType
 * @returns {string}
 */
export function parseAddrObjectType (addrObjType) {
  if (addrObjType === 1) {
    return 'host'
  } else if (addrObjType === 2 || addrObjType === 3) {
    return 'network'
  } else if (addrObjType === 4) {
    return 'group'
  } else {
    return 'domain'
  }
}

/**
 * 주소 객체 addrType addr_obj_type로 변환 (TAMS)
 *
 * @param addrType
 * @returns {int}
 */
export function parseAddrObjectTypeNumber (addrType) {
  if (addrType === 'host') {
    return 1
  } else if (addrType === 'network') {
    return 2
  } else if (addrType === 'group') {
    return 4
  } else {
    return 5
  }
}

/**
 * IP Version Type 변환 (TAMS)
 *
 * @param version
 * @returns {int}
 */
export function parseAddrObjectIpVersionType (version, addrType = '') {
  return addrType === 'domain' ? 3 : (version.toString() === '4') ? 1 : 2
}

/**
 * 주소 객체 팝업 사이즈
 * computed
 *
 * @returns {{minWidth: string, minHeight: string}}
 */
export function getAddressObjSize () {
  const
    addrType = getAddrObjType(this.objectType),
    mode = this.mode

  let size = {
    width: '700px',
    height: '350px'
  }

  if (addrType === 'host') {
    size = {
      width: '700px',
      height: mode === 'edit' ? '316px' : '271px'
    }
  }

  if (addrType === 'network') {
    size = {
      width: '774px',
      height: mode === 'edit' ? '350px' : '307px'
    }
  }

  if (addrType === 'domain') {
    size = {
      width: '700px',
      height: mode === 'edit' ? '790px' : '740px'
    }
  }

  if (addrType === 'group') {
    size = {
      width: '900px',
      height: mode === 'edit' ? '742px' : '635px'
    }
  }

  if (addrType === 'user') {
    if (this.objectType.indexOf('ipMapping') > -1) {
      size = {
        width: '700px',
        height: mode === 'edit' ? '635px' : '528px'
      }
    } else {
      size = {
        width: '700px',
        height: mode === 'edit' ? '560px' : '533px'
      }
    }
  }

  if (addrType === 'device') {
    size = {
      width: '700px',
      height: '416px'
    }
  }

  return size
}

/**
 * 주소 객체 타이틀
 * computed
 *
 * @returns {string}
 */
export function getAddressObjTitle () {
  const addrType = getAddrObjType(this.objectType)
  let title = ''

  if (addrType === 'host') {
    if (this.mode === 'edit') {
      title = this.$t('op.objectSettings_label_editHostObj')
    } else {
      title = this.$t('op.objectSettings_label_addHostObj')
    }
  }

  if (addrType === 'network') {
    if (this.mode === 'edit') {
      title = this.$t('op.objectSettings_label_editNetworkObj')
    } else {
      title = this.$t('op.objectSettings_label_addNetworkObj')
    }
  }

  if (addrType === 'group') {
    if (this.mode === 'edit') {
      title = this.$t('op.objectSettings_label_editGroupObj')
    } else {
      title = this.$t('op.objectSettings_label_addGroupObj')
    }
  }

  if (addrType === 'domain') {
    if (this.mode === 'edit') {
      title = this.$t('op.objectSettings_label_editDomainObj')
    } else {
      title = this.$t('op.objectSettings_label_addDomainObj')
    }
  }

  if (addrType === 'user') {
    if (this.mode === 'edit') {
      if (this.objectType.indexOf('ipMapping') > -1) {
        title = this.$t('op.objectSettings_label_editIpMapServer')
      } else {
        title = this.$t('op.objectSettings_label_editUser')
      }
    } else {
      if (this.objectType.indexOf('ipMapping') > -1) {
        title = this.$t('op.objectSettings_label_addIpMapServer')
      } else {
        title = this.$t('op.objectSettings_label_addUser')
      }
    }
  }

  if (addrType === 'device' && ['address.device.object', 'address.device'].indexOf(this.objectType) >= 0) {
    if (this.mode === 'edit') {
      title = this.$t('op.objectSettings_label_editDeviceObj')
    } else {
      title = this.$t('op.objectSettings_label_addDeviceObj')
    }
  }
  return title
}

/**
 * 네트워크 객체 데이터 설정
 *
 * @param addrType
 * @returns {*}
 */
export function computedSetNetworkData (storeState) {
  // TAMS
  const result = {
    addr_obj_uname: storeState.networkData.addr_obj_uname,
    addr_obj_zone_type: storeState.networkData.addr_obj_zone_type,
    addr_obj_type: storeState.networkData.addr_obj_type,
    addr_obj_desc: storeState.networkData.addr_obj_desc
  }

  if (result.addr_obj_type === 2) {
    _.assign(result, {
      // ip_list: {
      //   ip_info1: storeState.networkData.ipAddress.ip,
      //   ip_info2: storeState.networkData.ipAddress.netmask
      // }
      // TAMS
      addr_obj_ip_exp: `${storeState.networkData.ipAddress.ip}/${storeState.networkData.ipAddress.netmask}`
    })
  }

  if (result.addr_obj_type === 3) {
    _.assign(result, {
      // ip_list: {
      //   ip_info1: storeState.networkData.ipRange.ip1,
      //   ip_info2: storeState.networkData.ipRange.ip2
      // }
      addr_obj_ip_exp: `${storeState.networkData.ipRange.ip1}-${storeState.networkData.ipRange.ip2}`
    })
  }

  return result
}

/**
 * 네트워크 객체 데이터 조회
 *
 * @param data
 * @returns {{}}
 */
export function computedGetNetworkData (data) {
  let ipInfo = {}

  // TAMS, IP 주소(mask)
  if (data.addr_obj_type === 2) {
    const sp = data.addr_obj_ip_exp.split('/') // Netmask '/' -> 10.10.10.10/25
    ipInfo = {
      ipRange: {
        ip1: null,
        ip2: null
      },
      ipAddress: {
        // ip: data.ip_list.ip_info1,
        // netmask: data.ip_list.ip_info2
        // TAMS
        ip: sp[0],
        netmask: sp[1]
      }
    }
  }

  // TAMS, IP 주소 범위
  if (data.addr_obj_type === 3) {
    const sp = data.addr_obj_ip_exp.split('-') // Range '-' -> 10.10.10.10~12.12.12.12
    ipInfo = {
      ipRange: {
        // ip1: data.ip_list.ip_info1,
        // ip2: data.ip_list.ip_info2
        // TAMS
        ip1: sp[0],
        ip2: sp[1]
      },
      ipAddress: {
        ip: null,
        netmask: null
      }
    }
  }

  return {
    ...data,
    ...ipInfo
  }
}

/**
 * 도메인 객체 데이터 조회
 *
 * @param data
 * @returns {{name: *, url_addr: boolean, zone: *, ipv4: *, ipv6: *, dmn_name: *}}
 */
export function computedGetDomainData (data) {
  // TAMS 미사용
  // util로 뺄까?
  // const getIp = (ip) => _.map(ip, (o) => {
  //   return o.dmn_ip
  // })

  // TAMS
  const
    ipv4List = data.ipv4_ip_list !== null ? data.ipv4_ip_list.split(',') : [],
    ipv6List = data.ipv6_ip_list !== null ? data.ipv6_ip_list.split(',') : []

  return _.assign(data, {
    // fullIpv4: getIp(data.ipv4),
    // fullIpv6: getIp(data.ipv6),
    // ipv4_ori: getIp(data.ipv4),
    // ipv4: getIp(data.ipv4),
    // ipv6_ori: getIp(data.ipv6),
    // ipv6: getIp(data.ipv6)
    // TAMS
    fullIpv4: _.cloneDeep(ipv4List),
    fullIpv6: _.cloneDeep(ipv6List),
    ipv4_ori: _.cloneDeep(ipv4List),
    ipv4: _.cloneDeep(ipv4List),
    ipv6_ori: _.cloneDeep(ipv6List),
    ipv6: _.cloneDeep(ipv6List)
  })
}

/**
 * pre-define 객체 요소가 있는지 확인
 *
 * @param data: object || array
 * @returns boolean
 */
export function isPreDefine (data) {
  if (!data) return false
  if (_.isArray(data)) {
    for (let i = 0, l = data.length; i < l; i++) {
      if (_.get(data, [i, 'web_cate_obj_predef'], 0) === 1) return true // TAMS (predef -> web_cate_obj_predef)
      if (_.get(data, [i, 'pre_def'], 0) === 1) return true
      if (/^(Any_INT|Any_EXT|Any_DMZ)/.test(_.get(data, [i, 'name'], '')) && _.get(data, [i, 'addr_obj_type'], 0) === 2) return true
      if (/^(MS-Azure\(Any\)|AWS\(Any\))/.test(_.get(data, [i, 'name'], '')) && _.get(data, [i, 'addr_obj_type'], 0) === 7) return true
    }
  } else if (_.isObject(data)) {
    if (/^(Any_INT|Any_EXT|Any_DMZ)/.test(_.get(data, ['name'], '')) && _.get(data, ['addr_obj_type'], 0) === 2) return true
    if (/^(MS-Azure\(Any\)|AWS\(Any\))/.test(_.get(data, ['name'], '')) && _.get(data, ['addr_obj_type'], 0) === 7) return true
    if (_.get(data, ['web_cate_obj_predef'], 0) === 1) return true // TAMS (predef -> web_cate_obj_predef)
    return _.get(data, ['pre_def'], 0) === 1
  }
  return false
}

/**
 * app type으로 삭제 가능 요소 인지 확인
 *
 * @param data: object || array
 * @returns boolean
 */
export function isPreDefineWithApp (data) {
  if (!data) return false
  if (_.isArray(data)) {
    for (let i = 0, l = data.length; i < l; i++) {
      // TAMS (NGF: app_obj_type -> app_type, type -> app_obj_type)
      // "app_type": "(numeric) TYPE 1:객체, 2:그룹, 3:사용자 "
      // "app_obj_type": "(numeric) predefine:0 사용자:1 그룹:2, 앱 그룹:3"
      if (_.get(data, [i, 'app_obj_type'], 0) === 0) {
        return true
      }
    }
  } else if (_.isObject(data)) {
    // TAMS (NGF: app_obj_type -> app_type, type -> app_obj_type)
    // "app_type": "(numeric) TYPE 1:객체, 2:그룹, 3:사용자 "
    // "app_obj_type": "(numeric) predefine:0 사용자:1 그룹:2, 앱 그룹:3"
    return _.get(data, ['app_obj_type'], 0) === 0
  }
  return false
}

/**
 * 앱 객체 필터 데이터 가공
 *
 * @param appSearchFilter
 */

const getPriorityIndex = (filterItem, filterDic, filterKey) => {
  return _.find(filterItem, (o) => { return o[filterDic[filterKey]] === -1 && o.use === 1 })
}

export function appFilterSaveFormatData (appSearchFilter) {
  const result = {}
  const keyList = Object.keys(appSearchFilter)

  const filterDic = {
    appSearchCategory: 'app_cate_id',
    appSearchTech: 'app_tech_id',
    appSearchChar: 'app_char_id',
    appSearchTag: 'app_tag_id',
    appSearchCountry: 'app_cntry_id',
    appSearchRisk: 'app_risk_id'
  }
  for (let i = 0, l = keyList.length; i < l; i++) {
    const filterKey = keyList[i]
    const filterItem = appSearchFilter[filterKey]
    let useList = void 0
    const isAllObj = getPriorityIndex(filterItem, filterDic, filterKey)
    if (isAllObj) {
      useList = []
    } else {
      useList = filterItem.filter((o) => {
        if (o[filterDic[filterKey]] === -1) {
          return false
        }
        return o.use === 1
      })
    }

    result[filterKey] = _.map(useList, filterDic[filterKey])
  }

  return result
}

/**
 * 주소 객체 데이터 가공
 * getter
 *
 * @param obj
 */
const getAddrObjectType = (_obj) => {
  const obj = _obj
  if (!_.has(obj, 'ref_info') && _.has(obj, 'ref_cnt') && obj.ref_cnt > 0) {
    obj.ref_info = '{\"ref_cnt\":' + obj.ref_cnt + '}'
  }
  // TAMS 불필요
  // // host
  // if (obj.addr_obj_type === 1) {
  //   if (_.isArray(obj.ip_list)) {
  //     return _.assign(obj, {
  //       fullIp: obj.ip_list.join(',')
  //     })
  //   }
  // }

  // TAMS 불필요
  // // network netmask
  // if (obj.addr_obj_type === 2) {
  //   return _.assign(obj, {
  //     fullIp: `${obj.ip_list.ip_info1}/${obj.ip_list.ip_info2}`
  //   })
  // }

  // TAMS 불필요
  // // network range
  // if (obj.addr_obj_type === 3) {
  //   return _.assign(obj, {
  //     fullIp: `${obj.ip_list.ip_info1} ~ ${obj.ip_list.ip_info2}`
  //   })
  // }

  // group
  if (obj.addr_obj_type === 4) {
    return _.assign(obj, {
      children: [],
      level: 0
    })
  }

  // TAMS 불필요
  // // domain range
  // if (obj.addr_obj_type === 5) {
  //   let parseIpv4 = [], parseIpv6 = []
  //
  //   _.each(obj.ipv4, (ipv4) => {
  //     parseIpv4.push(ipv4.dmn_ip)
  //   })
  //
  //   _.each(obj.ipv6, (ipv6) => {
  //     parseIpv6.push(ipv6.dmn_ip)
  //   })
  //
  //   return _.assign(obj, {
  //     parseIpv4: _.join(parseIpv4, ','),
  //     parseIpv6: _.join(parseIpv6, ',')
  //   })
  // }
  return obj
}
const getUserObjectType = (_obj) => {
  const obj = _obj
  let
    parseName = void 0,
    userId = void 0,
    domain = void 0,
    groupName = void 0,
    parseType = void 0

  if (obj.usr_obj_type === 1) {
    parseName = obj.login_id
    userId = obj.login_id

    // 사용자 객체 추가 시
    if (_.has(obj, 'dmn')) {
      if (obj.dmn === '') {
        parseName = obj.login_id
      } else {
        parseName = `${obj.dmn}\\${obj.login_id}`
        userId = obj.login_id
        domain = obj.dmn
      }
    } else {
      const splitUserId = obj.login_id.split('\\')

      if (splitUserId.length > 1) {
        userId = splitUserId[1]
        domain = splitUserId[0]
      } else {
        userId = splitUserId[0]
      }
    }
  } else if (obj.usr_obj_type === 2) {
    // user group
    if (obj.usr_grp_dmn !== '') {
      parseName = `${obj.usr_grp_dmn}\\${obj.usr_grp_name}`
      groupName = obj.usr_grp_name
      domain = obj.usr_grp_dmn
    } else {
      parseName = obj.usr_grp_name
      groupName = obj.usr_grp_name
    }
  } else if (obj.usr_obj_type === 4) {
    // ip mapping user group
    parseName = obj.grp_name
    groupName = obj.grp_name
  } else if (obj.usr_obj_type === 6) {
    // ad user group
    parseName = obj.ad_map_grp

    const splitUserId = parseName.split('\\')

    if (splitUserId.length > 0) {
      groupName = splitUserId[1]
      domain = splitUserId[0]
    }
  } else if (obj.usr_obj_type === 3) {
    // ip mapping user
    parseName = obj.usr_id
    userId = obj.usr_id
  } else if (obj.usr_obj_type === 5) {
    // ad user
    parseName = obj.ad_map_usr
    userId = obj.ad_map_usr

    const splitUserId = obj.ad_map_usr.split('\\')

    if (splitUserId.length > 0) {
      userId = splitUserId[1]
      domain = splitUserId[0]
    }
  }

  if (_.has(obj, 'type')) {
    parseType = obj.type === 1 ? i18n.t('op.objectSettings_label_localDB') : i18n.t('cmn.outside')
  }

  return _.assign(obj, {parseName, userId, domain, groupName, parseType})
}
const getDeviceObjectType = (_obj) => {
  const obj = _obj
  if (!window.$nuxt) {
    return obj
  }

  const dict = {
    0: i18n.t('op.objectSettings_tooltip_deviceStatNOK'), // 비정상 (NOK)
    1: i18n.t('op.objectSettings_tooltip_deviceStatON'), // 온라인 (ON)
    2: i18n.t('op.objectSettings_tooltip_deviceStatOFF'), // 오프라인 (OFF)
    3: i18n.t('op.objectSettings_tooltip_deviceStatINIT'), // 로그인 (LOGIN)
    4: i18n.t('po.linkageServerSettings_label_dvc_stat_4'), // 삭제대기
    5: i18n.t('po.linkageServerSettings_label_dvc_stat_5'), // 삭제완료
    6: i18n.t('po.linkageServerSettings_label_dvc_stat_6') // 삭제요청
  }

  return _.assign(obj, {
    print_dvc_stat: dict[_.get(obj, ['dvc_stat'], 4)],
    print_dvc_ip: _.get(obj, ['dvc_ip'], []).map(o => o.ip).join(' ')
  })
}
export function objectSetDataParser (obj) {
  // 주소 객체
  if (_.has(obj, 'addr_obj_type')) {
    getAddrObjectType(obj)
  }

  if (_.has(obj, 'usr_obj_type')) {
    getUserObjectType(obj)
  }

  // device
  if (_.has(obj, 'dvc_obj_type')) {
    getDeviceObjectType(obj)
  }
  return obj
}

/**
 * 시간 객체 view data
 * getter
 *
 * @param timeObjectDataList
 */
export function getTimeObjectList (timeObjectDataList) {
  try {
    const result = _.map(_.cloneDeep(timeObjectDataList), (o) => {
      // TAMS
      if (!isJsonString(o.time_obj_set)) {
        return o
      }
      const p = o.time_obj_set = JSON.parse(o.time_obj_set)
      o.week = typeof p.week !== 'undefined' ? p.week : {}
      o.month = typeof p.month !== 'undefined' ? p.month : {}
      o.year = typeof p.year !== 'undefined' ? p.year : {}
      o.period = typeof p.period !== 'undefined' ? p.period : {}

      return _.assign(o, {
        parseWeek: weekFormatter(o.week),
        parseMonth: monthFormatter(o.month),
        parseYear: yearFormatter(o.year),
        parsePeriod: rangeFormatter(o.period)
      })
    })

    return result
  } catch (e) {
    window.$nuxt.$log('[Object Settings Utils GUI Error (getTimeObjectList)] ', e)
    return timeObjectDataList
  }
}

/**
 * 시간 객체 데이터 설정 (TAMS)
 *
 * @param storeTimeData
 * @returns {*}
 */
export function computedSetTimeData (storeTimeData) {
  let result = {}
  try {
    result = {
      time_obj_uname: storeTimeData.time_obj_uname,
      time_obj_desc: _.isUndefined(storeTimeData.time_obj_desc) ? '' : storeTimeData.time_obj_desc,
      time_obj_set: {
        week: storeTimeData.week,
        month: storeTimeData.month,
        year: storeTimeData.year,
        period: storeTimeData.period
      }
    }
  } catch (e) {
    window.$nuxt.$log('[Object Settings Utils GUI Error (computedSetTimeData)]', e)
  }
  return result
}

/**
 * 애플리케이션 그룹 객체 > 애플리케이션 객체
 * getter
 *
 * @param groupAppList
 */
export function parseAppObjDataList (groupAppList) {
  return groupAppList.filter((row) => {
    return String(row.name).toLowerCase().indexOf('') > -1 || String(row.app_cate).toLowerCase().indexOf('') > -1
  })
}

/**
 * 애플리케이션 그룹 객체 > 애플리케이션 그룹 객체 목록
 * getter
 *
 * @param groupAppList
 */
export function parseAppGroupTreeDataList (groupAppList) {
  return _.map(_.cloneDeep(groupAppList), (data) => {
    return _.assign(data, {
      children: [],
      level: 0
    })
  })
}

/**
 * 서비스 그룹 객체 > 서비스 객체
 * getter
 *
 * @param groupServiceList
 */
export function parseServiceObjDataList (groupServiceList) {
  return _.map(_.filter(_.cloneDeep(groupServiceList), o => o.srv_obj_id !== 0), (data) => {
    let result = null,
      protocolName = '',
      startPort = '',
      srvPort = '',
      lifeTime = ''

    if (_.has(data, 'prtc')) {
      protocolName = data.prtc.name
    }

    if (_.has(data, 'start_port')) {
      startPort = data.start_port
    }

    if (_.has(data, 'srv_port')) {
      srvPort = data.srv_port
    }

    if (_.has(data, 'life_time')) {
      lifeTime = data.life_time
    }

    result = `${protocolName} (${startPort} -> ${srvPort}), LT=${lifeTime}`

    return _.assign(data, {serviceInfo: result})
  })
}

/**
 * 서비스 그룹 객체 > 서비스 그룹 객체 목록
 * getter
 *
 * @param groupServiceList
 */
export function parseServiceGroupTreeDataList (groupServiceList) {
  return _.map(_.cloneDeep(groupServiceList), (data) => {
    return _.assign(data, {
      children: [],
      level: 0
    })
  })
}

/**
 * 디바이스 그룹 객체 - 보안 정책 - os 버전 옵션 목록
 * getter
 *
 * @param deviceGroupSecPolicyData
 */
export function getParseOSVersionOpts (deviceGroupSecPolicyData) {
  return _.map(deviceGroupSecPolicyData.osList, (o) => {
    if (o) {
      return _.assign(o, {
        label: `${o.name}(${o.ver})`,
        value: o.ver
      })
    }
  }).filter((o) => {
    return _.get(deviceGroupSecPolicyData, ['os', 'os_type'], false) === o.os_type
  })
}

/**
 * 애플리케이션 필터 객체 포맷
 * getter
 *
 * @param appFilterData
 */
export function filterGetFormData (appFilterData) {
  // TAMS
  return {
    app_cate_id_list: _.map(appFilterData.app_cate, (o) => { return o.app_cate_id }),
    app_tech_id_list: _.map(appFilterData.app_tech, (o) => { return o.app_tech_id }),
    app_char_id_list: _.map(appFilterData.app_char, (o) => { return o.app_char_id }),
    app_tag_id_list: _.map(appFilterData.app_tag, (o) => { return o.app_tag_id }),
    app_cntry_id_list: _.map(appFilterData.app_cntry, (o) => { return o.app_cntry_id }),
    app_risk_id_list: _.map(appFilterData.app_risk, (o) => { return o.app_risk_id })
  }
}

export const strict = true
